// Dial-list detail (v2). Replaces pages/DialListDetailPage.tsx.
//
// - Header: back, name, description, summary counts, Add contacts,
//   Delete list.
// - NextUpCard for the next pending entry — a focal call-to-action.
// - DataGrid showing every entry; status + notes editable inline,
//   Call action records an attempt and flips status to in_progress.
// - Bulk select (DataGrid built-in) → BulkActionBar with "Mark
//   reached", "Mark do-not-call", "Remove from list".
// - Add contacts opens a slide-over picker.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Contact,
  DialList,
  DialListEntry,
  DialListEntryStatus
} from '@personical/cc-shared-types';
import { TERMINAL_DIAL_STATUSES } from '@personical/cc-shared-types';
import { Avatar } from '../../components/Avatar.js';
import { useApi } from '../../components/api-context.js';
import { BulkAction, BulkActionBar } from '../../grid/BulkActionBar.js';
import { DataGrid } from '../../grid/DataGrid.js';
import {
  DIAL_ENTRY_COLUMNS,
  STATUS_LABEL,
  type DialEntryGridContext
} from '../../grid/columns/dialEntryColumns.js';
import { useDensity } from '../../grid/useDensity.js';
import { useToast } from '../../toast.js';
import { AddContactsPanel } from './AddContactsPanel.js';

type ListWithEntries = DialList & { entries: DialListEntry[] };

export function ListDetailView() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();
  const [density] = useDensity();

  const [list, setList] = useState<ListWithEntries | null>(null);
  const [contactsById, setContactsById] = useState<Record<string, Contact>>({});
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setList(await api.getDialList(id));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api, id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Hydrate Contact rows so the grid can show name/phone/avatar. 404s
  // are normal (the contact may have been hard-deleted while the entry
  // remained) — show "Contact removed" in that case.
  useEffect(() => {
    if (!list) return;
    const ids = new Set(list.entries.map((e) => e.contactId));
    const missing = [...ids].filter((cid) => !contactsById[cid]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const fetched: Record<string, Contact> = {};
      await Promise.all(
        missing.map(async (cid) => {
          try {
            fetched[cid] = await api.get(cid);
          } catch {
            // ignore — likely 404 for a deleted contact
          }
        })
      );
      if (!cancelled) setContactsById((prev) => ({ ...prev, ...fetched }));
    })();
    return () => {
      cancelled = true;
    };
  }, [api, list, contactsById]);

  const summary = useMemo(() => {
    if (!list) return null;
    const total = list.entries.length;
    const done = list.entries.filter((e) => TERMINAL_DIAL_STATUSES.includes(e.status)).length;
    const pending = list.entries.filter((e) => e.status === 'pending').length;
    return { total, done, pending };
  }, [list]);

  const nextPending = useMemo(() => {
    if (!list) return null;
    return (
      list.entries.find(
        (e) =>
          e.status === 'pending' ||
          e.status === 'no_answer' ||
          e.status === 'voicemail' ||
          e.status === 'rejected' ||
          e.status === 'skipped'
      ) ?? null
    );
  }, [list]);

  async function patchEntry(
    entryId: string,
    body: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }
  ): Promise<void> {
    if (!id) return;
    // Optimistic: update local state immediately, revert on error.
    const original = list?.entries.find((e) => e.id === entryId);
    if (original) {
      setList((prev) =>
        prev
          ? {
              ...prev,
              entries: prev.entries.map((e) =>
                e.id === entryId
                  ? { ...e, status: body.status ?? e.status, notes: body.notes ?? e.notes }
                  : e
              )
            }
          : prev
      );
    }
    try {
      const updated = await api.patchDialListEntry(id, entryId, body);
      setList((prev) =>
        prev
          ? {
              ...prev,
              entries: prev.entries.map((e) => (e.id === entryId ? updated : e))
            }
          : prev
      );
    } catch (err) {
      if (original) {
        setList((prev) =>
          prev
            ? {
                ...prev,
                entries: prev.entries.map((e) => (e.id === entryId ? original : e))
              }
            : prev
        );
      }
      toast.error((err as Error).message);
      throw err;
    }
  }

  async function removeEntry(entryId: string): Promise<void> {
    if (!id) return;
    try {
      await api.deleteDialListEntry(id, entryId);
      setList((prev) =>
        prev ? { ...prev, entries: prev.entries.filter((e) => e.id !== entryId) } : prev
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function deleteList() {
    if (!id) return;
    if (!confirm('Delete this dial list? Entries will be removed too.')) return;
    try {
      await api.deleteDialList(id);
      toast.success('Dial list deleted.');
      navigate('/dial');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function addContacts(contactIds: string[]) {
    if (!id) return;
    try {
      await api.addDialListEntries(id, contactIds);
      toast.success(`Added ${contactIds.length} ${contactIds.length === 1 ? 'contact' : 'contacts'}.`);
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
      throw err;
    }
  }

  async function bulkSet(status: DialListEntryStatus) {
    const ids = [...selected];
    if (ids.length === 0 || !id) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((entryId) => api.patchDialListEntry(id, entryId, { status })));
      await refresh();
      setSelected(new Set());
      toast.success(`Marked ${ids.length} as ${STATUS_LABEL[status]}.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  async function bulkRemove() {
    const ids = [...selected];
    if (ids.length === 0 || !id) return;
    if (!confirm(`Remove ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'} from the list?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((entryId) => api.deleteDialListEntry(id, entryId)));
      setList((prev) =>
        prev ? { ...prev, entries: prev.entries.filter((e) => !selected.has(e.id)) } : prev
      );
      setSelected(new Set());
      toast.success(`Removed ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </p>
        <Link to="/dial" className="text-sm text-mid hover:text-strong">
          ← Back to lists
        </Link>
      </div>
    );
  }
  if (!list) return <p className="text-sm text-muted">Loading…</p>;

  const existing = new Set(list.entries.map((e) => e.contactId));
  const ctx: DialEntryGridContext = {
    contactsById,
    patchEntry,
    removeEntry
  };

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <Link to="/dial" className="text-xs text-muted hover:text-strong">
            ← Back to lists
          </Link>
          <h1 className="text-xl font-semibold text-strong mt-1 flex items-center gap-2">
            {list.name}
            {list.visibility === 'private' && (
              <span className="rounded-md surface-muted text-mid text-[11px] px-1.5 py-0.5 font-medium">
                Private
              </span>
            )}
          </h1>
          {list.description && <p className="text-sm text-muted mt-0.5">{list.description}</p>}
          {summary && (
            <p className="text-xs text-faint mt-1 tabular-nums">
              {summary.done}/{summary.total} done · {summary.pending} pending
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="btn-primary px-3 py-1.5 text-sm font-medium"
          >
            Add contacts
          </button>
          <button
            type="button"
            onClick={deleteList}
            className="rounded-md border border-red-300 dark:border-red-900 bg-transparent text-red-700 dark:text-red-300 px-3 py-1.5 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            Delete list
          </button>
        </div>
      </div>

      {nextPending && contactsById[nextPending.contactId] && (
        <NextUpCard
          entry={nextPending}
          contact={contactsById[nextPending.contactId]!}
          onPatch={(body) => patchEntry(nextPending.id, body)}
        />
      )}

      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())} busy={bulkBusy}>
        <BulkAction onClick={() => bulkSet('reached')} disabled={bulkBusy}>
          Mark reached
        </BulkAction>
        <BulkAction onClick={() => bulkSet('do_not_call')} disabled={bulkBusy}>
          Mark do-not-call
        </BulkAction>
        <BulkAction onClick={bulkRemove} disabled={bulkBusy} variant="danger">
          Remove from list
        </BulkAction>
      </BulkActionBar>

      <DataGrid
        columns={DIAL_ENTRY_COLUMNS}
        rows={list.entries}
        rowKey={(r) => r.id}
        selection={{ selected, onChange: setSelected }}
        context={ctx}
        density={density}
        empty="No contacts in this list yet. Click Add contacts above."
      />

      <AddContactsPanel
        open={adding}
        onClose={() => setAdding(false)}
        existing={existing}
        onAdd={addContacts}
      />
    </div>
  );
}

function NextUpCard({
  entry,
  contact,
  onPatch
}: {
  entry: DialListEntry;
  contact: Contact;
  onPatch(body: { status?: DialListEntryStatus; notes?: string; recordAttempt?: boolean }): Promise<void>;
}) {
  const [notes, setNotes] = useState(entry.notes ?? '');
  const [outcome, setOutcome] = useState<DialListEntryStatus>('reached');
  useEffect(() => {
    setNotes(entry.notes ?? '');
  }, [entry.id, entry.notes]);
  const phone = contact.phoneNumbers[0];
  return (
    <section className="surface border border-divider rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Avatar name={contact.displayName} photoUrl={contact.photoUrl} size="lg" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-faint">Next up</p>
          <p className="text-lg font-semibold text-strong">{contact.displayName}</p>
          {phone && (
            <p className="text-sm text-mid tabular-nums">
              {phone.e164 ?? phone.number}
              {entry.attempts > 0 && (
                <span className="text-xs text-faint ml-2">
                  {entry.attempts} attempt{entry.attempts === 1 ? '' : 's'}
                </span>
              )}
            </p>
          )}
        </div>
        {phone && (
          <a
            href={`tel:${phone.e164 ?? phone.number}`}
            onClick={() => void onPatch({ recordAttempt: true, status: 'in_progress' })}
            className="btn-primary px-4 py-2 text-sm font-medium"
          >
            Call
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-2">
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as DialListEntryStatus)}
          className="select"
        >
          {(
            ['reached', 'no_answer', 'voicemail', 'rejected', 'wrong_number', 'do_not_call', 'skipped'] as DialListEntryStatus[]
          ).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Outcome / notes"
          className="input"
        />
        <button
          type="button"
          onClick={() => void onPatch({ status: outcome, notes })}
          className="btn-secondary px-3 py-2 text-sm font-medium"
        >
          Save outcome
        </button>
      </div>
    </section>
  );
}
